import { FC, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';

interface PublishVersionDialogProps {
  visible: boolean;
  onClose: () => void;
  onPublish: (description: string) => Promise<void>;
  draftConfigCount: number;
  draftHudLineCount: number;
}

export const PublishVersionDialog: FC<PublishVersionDialogProps> = ({
  visible,
  onClose,
  onPublish,
  draftConfigCount,
  draftHudLineCount,
}) => {
  const [description, setDescription] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePublish = async () => {
    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    setError(null);
    setIsPublishing(true);
    
    try {
      await onPublish(description.trim());
      setDescription('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish version');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleClose = () => {
    if (!isPublishing) {
      setDescription('');
      setError(null);
      onClose();
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button 
        label="Cancel" 
        className="p-button-secondary" 
        onClick={handleClose}
        disabled={isPublishing}
      />
      <Button 
        label="Publish" 
        className="p-button-primary" 
        onClick={handlePublish}
        disabled={isPublishing || !description.trim()}
        loading={isPublishing}
      />
    </div>
  );

  return (
    <Dialog
      header="Publish Version"
      visible={visible}
      onHide={handleClose}
      footer={footer}
      style={{ width: '500px' }}
      dismissableMask={!isPublishing}
      closable={!isPublishing}
    >
      <div className="flex flex-column gap-4">
        <div className="bg-blue-50 p-3 border-round">
          <div className="flex align-items-start gap-2">
            <div className="flex flex-column gap-2">
              <p className="m-0 text-sm font-semibold">
                Publishing:
              </p>
              <p className="m-0 text-sm font-semibold">
                - {draftConfigCount} configuration{draftConfigCount !== 1 ? 's' : ''}
              </p>
              <p className="m-0 text-sm font-semibold">
                - {draftHudLineCount} HUD line{draftHudLineCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-column gap-2">
          <label htmlFor="version-description" className="font-medium">
            Version Description<span className="text-red-500">*</span>
          </label>
          <InputTextarea
            id="version-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setError(null);
            }}
            placeholder="Enter a description..."
            rows={3}
            className="w-full border-1 border-solid border-gray-300"
            disabled={isPublishing}
            autoFocus
          />
          {error && (
            <small className="p-error">{error}</small>
          )}
        </div>
      </div>
    </Dialog>
  );
};

